// trigger preview deploy: refresh preview DB clone
import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  NavigationType,
  RouterProvider,
} from 'react-router-dom';
import { Partytown } from '@builder.io/partytown/react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { initMonitoring } from '@repo/monitoring';
import { ENVIRONMENT, SENTRY_DSN } from './assets/env_constants';
import App from './App';

const UPDATE_CHECK_INTERVAL_MS = 1000 * 60 * 60; // 1 hour

// Initialize monitoring with universal error rules
initMonitoring({
  dsn: SENTRY_DSN,
  environment: ENVIRONMENT,

  // To test error reporting in sandbox/development, uncomment:
  // captureErrors: true,

  errorRules: {
    captureOn5xx: true,
    captureOn4xx: false,
    captureNetworkErrors: false,
    captureTimeouts: true,
  },
  sentryOptions: {
    release: __APP_RELEASE__,
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
  },
});

const router = createBrowserRouter([
  {
    path: '/*',
    element: <App />,
  },
]);

const ServiceWorkerWrapper = ({ children }: { children: React.ReactNode }) => {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    onRegistered(r: any) {
      if (!r) return;

      r.update();

      setInterval(() => {
        r.update();
      }, UPDATE_CHECK_INTERVAL_MS);
    },
  });

  React.useEffect(() => {
    if (!needRefresh) return;

    const getSection = (pathname: string) => pathname.split('/')[1];

    let lastSection = getSection(router.state.location.pathname);
    const unsubscribe = router.subscribe((state) => {
      const nextSection = getSection(state.location.pathname);
      if (
        state.historyAction === NavigationType.Push &&
        nextSection !== lastSection
      ) {
        unsubscribe();
        updateServiceWorker(true);
      }
      lastSection = nextSection;
    });
    return unsubscribe;
  }, [needRefresh, updateServiceWorker]);

  return <>{children}</>;
};

const render = () => {
  const rootElement = document.getElementById('root');
  if (!rootElement) throw new Error('Failed to find the root element');

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ServiceWorkerWrapper>
        <Partytown forward={['dataLayer.push']} />
        <RouterProvider router={router} />
      </ServiceWorkerWrapper>
    </React.StrictMode>,
  );
};

render();
